import React, { type CSSProperties, useEffect, useRef, useState } from "react";
import "./product-promotion.css";
import type { DTOAppliedPromotionView, DTOProductPromotionView } from "../../../../generated/api-client";
import type { LocaleCode } from "@/i18n/locales";
import type { InterpolationValues } from "@/i18n/types";
import { formatCurrency } from "@/utils/format";
import { Icon } from "@/components/ui-components/icon";

type Promotion = Pick<DTOAppliedPromotionView, "discountType" | "discountValue" | "minimumBuyQuantity" | "bundleCount">;

interface PromotionCardProps {
    promotion: Promotion;
    quantity: number;
    regularUnitPrice: number;
    lineTotal: number;
    savings: number;
    locale: LocaleCode;
    t: (key: string, values?: InterpolationValues) => string;
    className?: string;
    style?: CSSProperties;
}

interface PromotionProgressCardProps {
    promotion: Pick<DTOProductPromotionView, "discountType" | "discountValue" | "minimumBuyQuantity">;
    quantity: number;
    regularUnitPrice: number;
    locale: LocaleCode;
    t: (key: string, values?: InterpolationValues) => string;
    onAddUnits: (units: number) => void;
    disabled?: boolean;
    className?: string;
    style?: CSSProperties;
}

function promotionLabel(promotion: Promotion, locale: LocaleCode, t: PromotionCardProps["t"]) {
    return promotion.discountType === "Fix"
        ? t("commerce.product.bundleOffer", {
            quantity: promotion.minimumBuyQuantity,
            price: formatCurrency(promotion.discountValue, locale)
        })
        : t("commerce.product.percentOffer", { percent: promotion.discountValue });
}

export function PromotionMotionSlot({ visible, children }: { visible: boolean; children: React.ReactNode }) {
    const contentRef = useRef(children);
    const [mounted, setMounted] = useState(visible);
    const [present, setPresent] = useState(visible);
    if (visible) contentRef.current = children;

    useEffect(() => {
        if (visible) {
            setMounted(true);
            const frame = window.requestAnimationFrame(() => setPresent(true));
            return () => window.cancelAnimationFrame(frame);
        }

        setPresent(false);
        const timer = window.setTimeout(() => setMounted(false), 320);
        return () => window.clearTimeout(timer);
    }, [visible]);

    if (!mounted) return null;
    return (
        <div className={`cart-motion-slot${present ? " is-visible" : ""}`}>
            <div className="cart-motion-slot__content">{contentRef.current}</div>
        </div>
    );
}

export function PromotionCard({
    promotion,
    quantity,
    regularUnitPrice,
    lineTotal,
    savings,
    locale,
    t,
    className = "cart-promotion",
    style
}: PromotionCardProps) {
    const regularTotal = Number((regularUnitPrice * quantity).toFixed(2));
    const bundleCount = Number(promotion.bundleCount ?? Math.floor(quantity / promotion.minimumBuyQuantity));
    const bundledUnits = Number((bundleCount * promotion.minimumBuyQuantity).toFixed(3));
    const remainderUnits = Number(Math.max(0, quantity - bundledUnits).toFixed(3));

    return (
        <section className={className} aria-label={t("commerce.cart.promotion")} style={style}>
            <div className="cart-promotion__heading">
                <Icon name="tag" size="sm" />
                <strong>{promotionLabel(promotion, locale, t)}</strong>
            </div>
            {promotion.discountType === "Fix" ? (
                <>
                    <div className="cart-promotion__row">
                        <span>{t("commerce.cart.bundleBreakdown", { bundles: bundleCount, units: bundledUnits })}</span>
                        <strong>{formatCurrency(bundleCount * promotion.discountValue, locale)}</strong>
                    </div>
                    {remainderUnits > 0 && (
                        <div className="cart-promotion__row">
                            <span>{t("commerce.cart.remainderBreakdown", { units: remainderUnits })}</span>
                            <strong>{formatCurrency(remainderUnits * regularUnitPrice, locale)}</strong>
                        </div>
                    )}
                </>
            ) : (
                <div className="cart-promotion__row">
                    <span>{t("commerce.cart.discountedUnits", { units: quantity })}</span>
                    <strong>{formatCurrency(lineTotal, locale)}</strong>
                </div>
            )}
            <div className="cart-promotion__row cart-promotion__row--comparison">
                <span>{t("commerce.cart.regularTotal")}</span>
                <del>{formatCurrency(regularTotal, locale)}</del>
            </div>
            <div className="cart-promotion__row cart-promotion__row--savings">
                <span>{t("commerce.cart.youSave")}</span>
                <strong>{formatCurrency(savings, locale)}</strong>
            </div>
        </section>
    );
}

export function PromotionProgressCard({
    promotion,
    quantity,
    regularUnitPrice,
    locale,
    t,
    onAddUnits,
    disabled = false,
    className = "cart-promotion-progress",
    style
}: PromotionProgressCardProps) {
    const progress = Number((quantity % promotion.minimumBuyQuantity).toFixed(3));
    const unitsToAdd = Number((promotion.minimumBuyQuantity - progress).toFixed(3));
    const cost = Number((promotion.discountValue - progress * regularUnitPrice).toFixed(2));

    return (
        <aside className={className} style={style}>
            <div>
                <strong>
                    {t("commerce.cart.addMoreForPromotion", {
                        units: unitsToAdd,
                        offer: promotionLabel(promotion, locale, t)
                    })}
                </strong>
                <span>
                    {cost >= 0
                        ? t("commerce.cart.promotionExtraCost", { amount: formatCurrency(cost, locale) })
                        : t("commerce.cart.promotionLowersTotal", { amount: formatCurrency(Math.abs(cost), locale) })}
                </span>
            </div>
            <button type="button" disabled={disabled} onClick={() => onAddUnits(unitsToAdd)}>
                {t("commerce.cart.addPromotionUnits", { units: unitsToAdd })}
            </button>
        </aside>
    );
}
